import { endpoints } from '../config/endpoints';
import { apiFetch } from './client';
import type { Skill, CreateSkillRequest, UpdateSkillRequest, SkillDetails, FileTreeNode } from '../types/skill.types';
import { getErrorMessage } from '../utils/error';


function normalizeSkill(raw: any): Skill {
  return {
    id: raw.id,
    userId: raw.userId ?? raw.user_id ?? null,
    skillName: raw.skillName ?? raw.name ?? '',
    skillType: raw.skillType ?? raw.skill_type ?? 'user',
    description: raw.description ?? null,
    filePath: raw.filePath ?? raw.file_path ?? '',
    metadata: raw.metadata ?? null,
    createdAt: raw.createdAt ?? raw.created_at ?? '',
    updatedAt: raw.updatedAt ?? raw.updated_at ?? '',
  } as Skill;
}

/**
 * Fetch user skills scoped to a workspace.
 */
export async function fetchWorkspaceSkills(
  workspaceId: string,
  params?: { limit?: number; offset?: number },
): Promise<{ skills: Skill[]; total: number }> {
  const queryParams = new URLSearchParams();

  if (params?.limit) queryParams.set('limit', params.limit.toString());
  if (params?.offset) queryParams.set('offset', params.offset.toString());

  const url = `${endpoints.workspaceSkills.list(workspaceId)}${queryParams.toString() ? `?${queryParams}` : ''}`;
  const response = await apiFetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch skills: ${response.statusText}`);
  }

  const result = await response.json();
  const skills = (result.skills ?? result.data ?? []).map(normalizeSkill);
  const total = result.pagination?.totalCount ?? result.total ?? skills.length;
  return { skills, total };
}

export async function fetchSkillContent(id: string, workspaceId: string): Promise<string> {
  const response = await apiFetch(endpoints.workspaceSkills.getById(workspaceId, id));

  if (!response.ok) {
    throw new Error(`Failed to fetch skill content: ${response.statusText}`);
  }

  const data = await response.json();
  const content =
    data.content ??
    data.skillContent ??
    data.skill?.content ??
    data.skill?.skillContent ??
    data.data?.content ??
    data.data?.skillContent ??
    '';
  return content;
}

export async function fetchSkillDetails(
  id: string,
  workspaceId: string,
): Promise<SkillDetails> {
  const response = await apiFetch(endpoints.workspaceSkills.getById(workspaceId, id));

  if (!response.ok) {
    throw new Error(`Failed to fetch skill details: ${response.statusText}`);
  }

  const data = await response.json();
  const skill = data.skill ?? data.data ?? data;
  const content =
    skill?.content ??
    skill?.skillContent ??
    data.content ??
    data.skillContent ??
    data.data?.content ??
    data.data?.skillContent ??
    '';
  const fileTree: FileTreeNode[] | undefined =
    skill?.fileTree ?? data.fileTree ?? data.data?.fileTree ?? undefined;
  const hasAdditionalFiles: boolean | undefined =
    skill?.hasAdditionalFiles ?? data.hasAdditionalFiles ?? data.data?.hasAdditionalFiles;

  return {
    content,
    fileTree,
    hasAdditionalFiles,
  };
}

/**
 * Create a user skill within a workspace.
 * Maps frontend field names to backend expected names.
 */
export async function createSkill(workspaceId: string, data: CreateSkillRequest): Promise<Skill> {
  const body = {
    name: data.skillName,
    description: data.description || data.skillName,
    content: data.content,
    skillType: 'user',
  };

  const response = await apiFetch(endpoints.workspaceSkills.create(workspaceId), {
    method: 'POST',
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(getErrorMessage(error, `Failed to create skill: ${response.statusText}`));
  }

  const created = await response.json();
  return normalizeSkill(created);
}

/**
 * Update a user skill within a workspace.
 */
export async function updateSkill(workspaceId: string, id: string, data: UpdateSkillRequest): Promise<Skill> {
  const body: Record<string, unknown> = {};
  if (data.skillName !== undefined) body.name = data.skillName;
  if (data.description !== undefined) body.description = data.description;
  if (data.content !== undefined) body.content = data.content;

  const response = await apiFetch(endpoints.workspaceSkills.update(workspaceId, id), {
    method: 'PUT',
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(getErrorMessage(error, `Failed to update skill: ${response.statusText}`));
  }

  const updated = await response.json();
  return normalizeSkill(updated);
}

/**
 * Delete a user skill within a workspace.
 */
export async function deleteSkill(workspaceId: string, id: string): Promise<void> {
  const response = await apiFetch(endpoints.workspaceSkills.delete(workspaceId, id), {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`Failed to delete skill: ${response.statusText}`);
  }
}

/**
 * Download an individual asset file from a skill directory.
 * Returns the file as a Blob.
 */
export async function downloadSkillAsset(
  workspaceId: string,
  skillId: string,
  assetPath: string,
): Promise<Blob> {
  const url = `${endpoints.workspaceSkills.getById(workspaceId, skillId)}/assets?path=${encodeURIComponent(assetPath)}`;
  const response = await apiFetch(url);

  if (!response.ok) {
    throw new Error(`Failed to download skill asset "${assetPath}": ${response.statusText}`);
  }

  return response.blob();
}

/**
 * Find a skill ID by name within a workspace.
 */
export async function findSkillIdByName(
  workspaceId: string,
  skillName: string,
): Promise<string | null> {
  const { skills } = await fetchWorkspaceSkills(workspaceId, { limit: 100 });
  const lowerName = skillName.toLowerCase();
  const match =
    skills.find(s => s.skillName === skillName) ||
    skills.find(s => s.skillName?.toLowerCase() === lowerName) ||
    skills.find(s => s.skillName?.toLowerCase().includes(lowerName));
  return match?.id ?? null;
}

/**
 * Import a skill from file contents into a workspace.
 */
export async function importSkill(
  workspaceId: string,
  data: { skillName: string; files: Record<string, string> },
): Promise<Skill> {
  const body = {
    name: data.skillName,
    description: data.skillName,
    skillType: 'user',
    files: data.files,
  };

  const response = await apiFetch(endpoints.workspaceSkills.create(workspaceId), {
    method: 'POST',
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(getErrorMessage(error, `Failed to import skill: ${response.statusText}`));
  }

  const created = await response.json();
  return normalizeSkill(created);
}

/**
 * Import a skill from a ZIP file into a workspace.
 */
export async function importSkillZip(
  workspaceId: string,
  file: File,
  skillName?: string,
): Promise<Skill> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('skillType', 'user');
  if (skillName) {
    formData.append('skillName', skillName);
  }

  const response = await apiFetch(endpoints.workspaceSkills.upload(workspaceId), {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(getErrorMessage(error, `Failed to import skill ZIP: ${response.statusText}`));
  }

  const created = await response.json();
  return normalizeSkill(created);
}
